import { useParams } from '@tanstack/react-router';
import PageContent from '@zeus/core/layout/PageContent';
import PageTitle from '@zeus/core/layout/PageTitle';
import PublicTokenModal from '@zeus/core/modals/PublicTokenModal';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useOrganizationDeliveries } from '_/hooks/useDeliveries';
import useOrgAccess from '_/hooks/useOrgAccess';
import { usePublicTokens, useCreatePublicToken, useCopyToClipboard } from '_/hooks/usePublicTokens';
import { flattenInfiniteItems } from '_/utils/infiniteQuery';
import { SOURCE_ENGINEER_NOT_INCLUDED } from '_/utils/userManagement';
import { Button } from 'antd';
import React, { Fragment, useState, useEffect, ReactElement, useMemo } from 'react';

import DeliverySummaryItem from './DeliverySummary/DeliverySummaryItem';

function OrganizationDeliveries(): ReactElement {
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const params = useParams({ strict: false }) as Partial<{ publicToken?: string }>;
  const publicToken = params.publicToken || '';
  const canShareDeliveries = useOrgAccess(SOURCE_ENGINEER_NOT_INCLUDED);
  const { setActionButtons } = useTopHeader();

  const [isPublicTokenModalVisible, setIsPublicTokenModalVisible] = useState(false);
  const [scrollPosition, setScrollPosition] = useState<number | null>(null);

  // Fetch organization deliveries with pagination
  const {
    data: deliveriesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useOrganizationDeliveries(selectedOrganization?.id, undefined);

  // Fetch public tokens
  const { data: publicTokens = [] } = usePublicTokens();
  const createPublicTokenMutation = useCreatePublicToken();
  const copyToClipboardMutation = useCopyToClipboard();

  // Flatten all pages of deliveries into a single array
  const deliveries = useMemo(() => {
    return flattenInfiniteItems(deliveriesData);
  }, [deliveriesData]);

  useEffect(() => {
    if (scrollPosition) {
      const orgDeliveriesContainer = document.getElementById('org-deliveries-container');
      orgDeliveriesContainer?.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    }
  }, [deliveries, scrollPosition]);

  async function loadMoreDeliveries() {
    const orgDeliveriesContainer = document.getElementById('org-deliveries-container');
    if (orgDeliveriesContainer) {
      setScrollPosition(orgDeliveriesContainer.scrollTop);
    }
    if (hasNextPage) {
      await fetchNextPage();
    }
  }

  const togglePublicToken = () => {
    setIsPublicTokenModalVisible((prev) => !prev);
  };

  const handleShareDelivery = async () => {
    if (!publicTokens?.length) {
      const name = 'Default';
      await createPublicTokenMutation.mutateAsync({ name: name });
    }
    togglePublicToken();
  };

  const handlePublicTokenDownloadConfirm = (url: string) => {
    copyToClipboardMutation.mutate(url);
  };

  const shareButton = useMemo(() => {
    const disabled = !canShareDeliveries;
    let tooltipTitle: string;

    if (!deliveries?.length) {
      tooltipTitle = 'No deliveries to share';
    } else if (disabled) {
      tooltipTitle = 'You do not have the right permissions to Share Deliveries';
    } else {
      tooltipTitle = 'Share deliveries';
    }

    return {
      onClick: handleShareDelivery,
      icon: 'share_icon',
      tooltipTitle: tooltipTitle,
      disabled: disabled,
    };
  }, [canShareDeliveries, deliveries?.length, handleShareDelivery]);

  const viewMoreButton = !hasNextPage ? null : (
    <div style={{ width: '20%', textAlign: 'center' }}>
      <Button type="default" block onClick={loadMoreDeliveries} loading={isFetchingNextPage}>
        View More
      </Button>
    </div>
  );

  useEffect(() => {
    setActionButtons(publicToken ? [] : [shareButton]);

    return () => {
      setActionButtons([]);
    };
  }, [publicToken, shareButton, setActionButtons]);

  return (
    <>
      <PageTitle title="Deliveries" />
      <PageContent>
        <div
          id="org-deliveries-container"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: 'calc(100vh - 150px)',
            overflow: 'auto',
          }}
        >
          {deliveries && deliveries?.length ? (
            <Fragment>
              {deliveries?.map((delivery, idx) => (
                <DeliverySummaryItem key={delivery.id} id={`delivery-${idx}`} delivery={delivery} flow={delivery.flow} />
              ))}
              {viewMoreButton}
            </Fragment>
          ) : (
            <div>No Deliveries to Show</div>
          )}
        </div>
      </PageContent>

      <PublicTokenModal
        handleCancel={togglePublicToken}
        handleConfirm={togglePublicToken}
        handlePublicTokenDownloadConfirm={handlePublicTokenDownloadConfirm}
        open={isPublicTokenModalVisible}
        publicTokens={publicTokens}
        selectedOrgSlug={selectedOrgSlug}
      />
    </>
  );
}

export default OrganizationDeliveries;
